import api from './client';
import type { GrandProjet, PLQEntry, VersionListItem, VersionDetail, SnapshotVersion, SnapshotDetail, VersionDiff } from '../types/estimation';
import type { PaginatedResponse, PaginationParams } from '../types/pagination';

// ---------------------------------------------------------------------------
// GrandProjet CRUD
// ---------------------------------------------------------------------------

export async function listProjects(params?: { is_custom?: boolean; organization_id?: string; mine?: boolean }) {
  const res = await api.get<PaginatedResponse<GrandProjet>>('/projects', { params });
  // Backward compat: if response is paginated, extract items; if array, return as-is
  if (Array.isArray(res.data)) return res.data;
  return res.data.items;
}

export async function listProjectsPaginated(
  params?: PaginationParams & { is_custom?: boolean; organization_id?: string; mine?: boolean },
) {
  const res = await api.get<PaginatedResponse<GrandProjet>>('/projects', { params });
  return res.data;
}

export async function getProject(id: string) {
  const res = await api.get<GrandProjet>(`/projects/${id}`);
  return res.data;
}

export async function createProject(data: Partial<GrandProjet> & { name: string; change_summary?: string }) {
  const res = await api.post<GrandProjet>('/projects', data);
  return res.data;
}

export async function updateProject(id: string, data: Partial<GrandProjet> & { change_summary: string }) {
  const res = await api.put<GrandProjet>(`/projects/${id}`, data);
  return res.data;
}

export async function deleteProject(id: string) {
  const res = await api.delete(`/projects/${id}`);
  return res.data;
}

export async function forkProject(projectId: string, name?: string) {
  const res = await api.post<GrandProjet>(`/projects/${projectId}/fork`, null, {
    params: name ? { name } : undefined,
  });
  return res.data;
}

export async function listProjectForks(projectId: string) {
  const res = await api.get<GrandProjet[]>(`/projects/${projectId}/forks`);
  return res.data;
}

export async function listProjectVersions(projectId: string) {
  const res = await api.get<VersionListItem[]>(`/projects/${projectId}/versions`);
  return res.data;
}

export async function getProjectVersion(projectId: string, versionNumber: number) {
  const res = await api.get<VersionDetail>(`/projects/${projectId}/versions/${versionNumber}`);
  return res.data;
}

export async function rollbackProject(projectId: string, targetVersion: number, changeSummary?: string) {
  const res = await api.post<GrandProjet>(`/projects/${projectId}/rollback`, {
    target_version: targetVersion,
    change_summary: changeSummary,
  });
  return res.data;
}

export async function diffProjectVersions(projectId: string, versionA: number, versionB: number) {
  const res = await api.get(`/projects/${projectId}/versions/diff`, {
    params: { version_a: versionA, version_b: versionB },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Project Estimation Snapshots
// ---------------------------------------------------------------------------

export async function saveProjectSnapshot(projectId: string, data: {
  snapshot_name?: string;
  estimation_data: Record<string, unknown>;
  n2_overrides?: Record<string, number> | null;
  change_summary?: string;
}) {
  const res = await api.post(`/projects/${projectId}/snapshot`, data);
  return res.data;
}

export async function listProjectSnapshots(projectId: string) {
  const res = await api.get<SnapshotVersion[]>(`/projects/${projectId}/snapshots`);
  return res.data;
}

export async function getProjectSnapshot(projectId: string, versionNumber: number) {
  const res = await api.get<SnapshotDetail>(`/projects/${projectId}/snapshots/${versionNumber}`);
  return res.data;
}

export async function rollbackProjectSnapshot(projectId: string, targetVersion: number, changeSummary?: string) {
  const res = await api.post<SnapshotDetail>(`/projects/${projectId}/snapshot-rollback`, {
    target_version: targetVersion,
    change_summary: changeSummary,
  });
  return res.data;
}

export async function diffProjectSnapshots(projectId: string, versionA: number, versionB: number) {
  const res = await api.get<VersionDiff>(`/projects/${projectId}/snapshots/diff`, {
    params: { version_a: versionA, version_b: versionB },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Project Actuals (Plan-vs-Reality)
// ---------------------------------------------------------------------------

export interface ProjectActual {
  id: string;
  project_id: string;
  period: string;
  logements_delivered: number | null;
  cost_spent: number | null;
  emplois_created: number | null;
  sbp_built: number | null;
  inhabitants_moved: number | null;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export async function listProjectActuals(projectId: string) {
  const res = await api.get<ProjectActual[]>(`/projects/${projectId}/actuals`);
  return res.data;
}

export async function createProjectActual(projectId: string, data: {
  period: string;
  logements_delivered?: number | null;
  cost_spent?: number | null;
  emplois_created?: number | null;
  sbp_built?: number | null;
  inhabitants_moved?: number | null;
  notes?: string | null;
}) {
  const res = await api.post<ProjectActual>(`/projects/${projectId}/actuals`, data);
  return res.data;
}

export async function updateProjectActual(projectId: string, actualId: string, data: {
  logements_delivered?: number | null;
  cost_spent?: number | null;
  emplois_created?: number | null;
  sbp_built?: number | null;
  inhabitants_moved?: number | null;
  notes?: string | null;
}) {
  const res = await api.put<ProjectActual>(`/projects/${projectId}/actuals/${actualId}`, data);
  return res.data;
}

export async function deleteProjectActual(projectId: string, actualId: string) {
  await api.delete(`/projects/${projectId}/actuals/${actualId}`);
}

// ---------------------------------------------------------------------------
// Project Collaborators
// ---------------------------------------------------------------------------

export interface ProjectCollaborator {
  id: string;
  project_id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  permission: string;
  granted_by_user_id: string;
  granted_by_name: string | null;
  created_at: string;
}

export async function listCollaborators(projectId: string) {
  const res = await api.get<ProjectCollaborator[]>(`/projects/${projectId}/collaborators`);
  return res.data;
}

export async function addCollaborator(projectId: string, userId: string, permission: string) {
  const res = await api.post<ProjectCollaborator>(`/projects/${projectId}/collaborators`, {
    user_id: userId,
    permission,
  });
  return res.data;
}

export async function updateCollaborator(projectId: string, userId: string, permission: string) {
  const res = await api.put<ProjectCollaborator>(`/projects/${projectId}/collaborators/${userId}`, {
    permission,
  });
  return res.data;
}

export async function removeCollaborator(projectId: string, userId: string) {
  await api.delete(`/projects/${projectId}/collaborators/${userId}`);
}

export async function bulkAddCollaborators(projectIds: string[], userId: string, permission: string) {
  const res = await api.post<{ added: number; updated: number }>('/projects/bulk-collaborators', {
    project_ids: projectIds,
    user_id: userId,
    permission,
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// PLQ Entry CRUD
// ---------------------------------------------------------------------------

export async function listPLQEntries(projectId?: string) {
  const res = await api.get<PLQEntry[]>('/plq', {
    params: projectId ? { project_id: projectId } : undefined,
  });
  return res.data;
}

export async function getPLQEntry(id: string) {
  const res = await api.get<PLQEntry>(`/plq/${id}`);
  return res.data;
}

export async function createPLQEntry(data: Partial<PLQEntry> & { source: string; change_summary: string }) {
  const res = await api.post<PLQEntry>('/plq', data);
  return res.data;
}

export async function updatePLQEntry(id: string, data: Partial<PLQEntry> & { change_summary: string }) {
  const res = await api.put<PLQEntry>(`/plq/${id}`, data);
  return res.data;
}

export async function deletePLQEntry(id: string) {
  const res = await api.delete(`/plq/${id}`);
  return res.data;
}

export async function listPLQVersions(entryId: string) {
  const res = await api.get<VersionListItem[]>(`/plq/${entryId}/versions`);
  return res.data;
}

export async function rollbackPLQEntry(entryId: string, targetVersion: number, changeSummary?: string) {
  const res = await api.post<PLQEntry>(`/plq/${entryId}/rollback`, {
    target_version: targetVersion,
    change_summary: changeSummary,
  });
  return res.data;
}
